import type { CloudVault } from '../vault/cloudVault';
import type { TagColorId } from '../design/tagPalette';
import { tagColorOf } from './tagColor';

/**
 * Folders, from the tag shards.
 *
 * A folder is a tag that has been CURATED — `curatedAs` is the whole
 * difference between the 33 folders and the ~6,800 other tags, most of them
 * machine-minted descriptors. Nothing else distinguishes them, which is why
 * this reads the shards rather than the `_tags.md` registry the head used
 * before: that file carries a name and a colour and has no idea which of its
 * rows is a place you can go. It also stopped being written on 2026-08-24
 * (`FileVaultExporter.WRITE_LEGACY_TAG_REGISTRY = false`), so a folder minted
 * since then is not in it at all.
 *
 * Sixteen known paths, ~1 MB, no directory listing — the shard set is a frozen
 * on-disk contract (`TagShards.BUCKETS`), so a reader that wants the whole
 * registry names the files.
 */

/** One row of `_tags/kNN.jsonl`, as `TagShards.Row` writes it. Only the fields
 *  this head reads are named; the rest ride along unparsed. */
export interface TagRow {
  id: string;
  /** Name. Ten live rows carry none — they are dropped rather than shown blank. */
  n?: string;
  /** A colour id — one of the palette's, or one the old palette stored — read
   *  by [tagColorOf]. */
  col?: string | null;
  /** An Android drawable name — `ic_book.xml`. Ten live folders carry one. */
  icon?: string | null;
  /** Parent TAG id — not necessarily a folder's. See [buildFolders]. */
  p?: string | null;
  /** `notebook` or `vertical`. Its presence is what makes the row a folder. */
  cur?: string | null;
}

/** A tag's own identity, or the nearest ancestor's where it has none. */
export interface Identity {
  /** The stored id the colour comes from, as stored: this tag's own, or the
   *  nearest ancestor's where its own names no colour. */
  color: string | null;
  icon: string | null;
  /** False when the icon came from above — some surfaces draw an inherited
   *  COLOUR and still want to know the icon is not this folder's own. */
  iconIsOwn: boolean;
  /** The tag the colour is inherited from, by name; null where it is this
   *  tag's own, or there is none. */
  colorFrom: string | null;
}

/**
 * Identity for every tag, inherited from the nearest ancestor that has one.
 *
 * The app's rule (`TagManagerImpl`, `TagEntryWithAncestry`):
 * `tag.color ?: ancestry.lastOrNull { it.color != null }`, where the path runs
 * outermost-first so the LAST match is the closest. It is what the app teaches
 * in its own words — *"Tags can also contain other tags. They will inherit
 * their parent's icon/color, unless you give them their own"*.
 *
 * A colour counts only where it names one of the palette's ([tagColorOf]): an
 * id that reads as no colour is none of the tag's own, so the tag wears its
 * nearest ancestor's rather than going untinted under a coloured parent.
 *
 * Resolved over ALL rows rather than over the folder tree, and that difference
 * is load-bearing: five folders sit under an ORDINARY tag, and the folder tree
 * deliberately surfaces those at the top because an uncurated parent is not a
 * place. It is still an ancestor for identity, so dropping it here would give
 * `cooking / еда` a different colour on the web than on the phone.
 */
export function resolveIdentities(rows: readonly TagRow[]): Map<string, Identity> {
  const byId = new Map(rows.map((row) => [row.id, row] as const));

  /** Self first, then up. A cycle stops the walk rather than hanging it. */
  const ancestry = (row: TagRow): TagRow[] => {
    const chain: TagRow[] = [];
    const seen = new Set<string>();
    let at: TagRow | undefined = row;
    while (at && !seen.has(at.id)) {
      seen.add(at.id);
      chain.push(at);
      at = at.p ? byId.get(at.p) : undefined;
    }
    return chain;
  };

  const out = new Map<string, Identity>();
  for (const row of rows) {
    const chain = ancestry(row);
    const source = chain.find((up) => tagColorOf(up.col) !== null);
    out.set(row.id, {
      color: source?.col ?? null,
      colorFrom: source && source.id !== row.id ? (source.n?.trim() ?? null) : null,
      icon: chain.map((up) => iconKeyOf(up.icon)).find((key) => key !== null) ?? null,
      iconIsOwn: iconKeyOf(row.icon) !== null,
    });
  }
  return out;
}

/**
 * Whether a tag is a NOTEBOOK: a place of its own rather than a folder inside
 * one. The one rule every surface asks — a tag with no parent, or with its
 * OWN colour or icon. Inherited identity does not count: a child wearing its
 * parent's colour is still inside that parent, and neither does a stored id
 * that reads as no colour.
 *
 * Read off the raw tag row, so `p` is the tag parent whether or not that
 * parent is curated: a folder under an ordinary tag is still inside it.
 */
export function isNotebook(tag: Pick<TagRow, 'p' | 'col' | 'icon'>): boolean {
  return !tag.p || tagColorOf(tag.col) !== null || iconKeyOf(tag.icon) !== null;
}

/** How the app curated the tag (`curatedAs`); a vertical is what the app
 *  writes a distilled render for. Not whether it is a notebook — that is
 *  [isNotebook]. */
export type FolderKind = 'notebook' | 'vertical';

export interface Folder {
  id: string;
  name: string;
  kind: FolderKind;
  /** [isNotebook], for this folder's own row. */
  notebook: boolean;
  /** The stored id, INHERITED where the folder has none of its own. */
  color: string | null;
  /** That id as one of the palette's colours, or null — only 8 carry one, but
   *  inheritance spreads those 8 over most of the tree. */
  hue: TagColorId | null;
  /** The drawable's bare name (`ic_book.xml` -> `book`), for [Mark]'s icon
   *  table, inherited the same way. Null where this build draws nothing for
   *  it, which is the same outcome as carrying no icon and needs no separate
   *  case. */
  icon: string | null;
  /** False when the icon came from an ancestor rather than from this folder. */
  iconIsOwn: boolean;
  /** The tag its colour is inherited from, by name; null where the colour is
   *  its own, or it has none. */
  colorFrom: string | null;
  /** The folder above, or null at the top. */
  parentId: string | null;
  /** How deep it sits, 0 at the top — so a flat listing can indent without
   *  walking back up per row. */
  depth: number;
  children: Folder[];
}

export interface Folders {
  roots: Folder[];
  byId: Map<string, Folder>;
  /** Lower-cased name → folder, for the chips, which carry names and not ids.
   *  Where two folders share a name neither is reachable this way rather than
   *  the wrong one being opened — a name is a convenience, the id is identity. */
  byName: Map<string, Folder>;
  /** Lower-cased tag name → the folder it files under: itself where it is a
   *  folder, otherwise its nearest folder ancestor. Absent for a tag with none. */
  homeOf: Map<string, Folder>;
  /** Every folder, depth-first in display order. */
  all: Folder[];
  /** Every notebook, wherever it sits, alphabetical — what the root lists. */
  notebooks: Folder[];
}

export const EMPTY_FOLDERS: Folders = { roots: [], byId: new Map(), byName: new Map(), homeOf: new Map(), all: [], notebooks: [] };

const BUCKETS = 16;

/** Every shard path. FROZEN alongside `TagShards.BUCKETS` — re-homing rows is a
 *  migration on the writing side, not an edit here. */
export const TAG_SHARDS: string[] = Array.from(
  { length: BUCKETS },
  (_, i) => `_tags/k${String(i).padStart(2, '0')}.jsonl`,
);

/** A line that will not parse costs one tag, never the shard — the same law the
 *  app's own decoder follows. */
export function parseTagShards(texts: readonly (string | null | undefined)[]): TagRow[] {
  const byId = new Map<string, TagRow>();
  for (const text of texts) {
    if (!text) continue;
    for (const line of text.split('\n')) {
      if (!line.trim()) continue;
      try {
        const row = JSON.parse(line) as TagRow;
        if (row?.id) byId.set(row.id, row);
      } catch {
        // Half a line is not a reason to lose the other thousand.
      }
    }
  }
  return [...byId.values()];
}

export async function readTagShards(vault: CloudVault): Promise<TagRow[]> {
  const files = await vault.readFiles(TAG_SHARDS);
  return parseTagShards(files.map((f) => f.text));
}

/** `ic_tag_forest_double.xml` -> `tag_forest_double`. The app names its
 *  drawables; the head draws its own, so only the middle is identity. */
export function iconKeyOf(icon: string | null | undefined): string | null {
  if (!icon) return null;
  const key = icon.replace(/\.xml$/, '').replace(/^ic_/, '').trim();
  return key === '' ? null : key;
}

function kindOf(cur: string | null | undefined): FolderKind | null {
  return cur === 'notebook' || cur === 'vertical' ? cur : null;
}

/**
 * The folder tree.
 *
 * Built over CURATED rows only. A folder's parent is its NEAREST CURATED
 * ancestor: an ordinary tag in between is not a place, so it is walked through
 * rather than stopped at — `осанка / posture` sits under `body`, a plain tag,
 * which sits under `health`, so posture is inside health. Only a folder with
 * no curated ancestor at all is at the top (`cooking / еда`, under `todo`
 * alone).
 *
 * A parent missing from the shards read ends the walk the same way, so an
 * unresolvable chain surfaces at the top rather than stranding the folder.
 */
export function buildFolders(rows: readonly TagRow[]): Folders {
  const identities = resolveIdentities(rows);
  const byId = new Map<string, Folder>();
  for (const row of rows) {
    const kind = kindOf(row.cur);
    const name = row.n?.trim();
    if (!kind || !name) continue;
    const identity = identities.get(row.id) ?? { color: null, icon: null, iconIsOwn: false, colorFrom: null };
    byId.set(row.id, {
      id: row.id,
      name,
      kind,
      notebook: isNotebook(row),
      color: identity.color,
      hue: tagColorOf(identity.color),
      icon: identity.icon,
      iconIsOwn: identity.iconIsOwn,
      colorFrom: identity.colorFrom,
      parentId: null,
      depth: 0,
      children: [],
    });
  }

  const rowById = new Map(rows.map((row) => [row.id, row] as const));
  /** Up through plain tags to the first curated one. A cycle, or one that
   *  leads back to the folder itself, is no parent. */
  const curatedAncestor = (row: TagRow): Folder | undefined => {
    const seen = new Set([row.id]);
    let at = row.p ?? undefined;
    while (at && !seen.has(at)) {
      seen.add(at);
      const folder = byId.get(at);
      if (folder) return folder;
      at = rowById.get(at)?.p ?? undefined;
    }
    return undefined;
  };

  const roots: Folder[] = [];
  for (const row of rows) {
    const folder = byId.get(row.id);
    if (!folder) continue;
    const parent = curatedAncestor(row);
    if (parent) {
      folder.parentId = parent.id;
      parent.children.push(folder);
    } else {
      roots.push(folder);
    }
  }

  const byName = new Map<string, Folder>();
  const collisions = new Set<string>();
  for (const folder of byId.values()) {
    const key = folder.name.toLowerCase();
    if (byName.has(key)) collisions.add(key);
    byName.set(key, folder);
  }
  for (const key of collisions) byName.delete(key);

  const homeOf = new Map<string, Folder>();
  for (const row of rows) {
    const name = row.n?.trim().toLowerCase();
    if (!name) continue;
    const home = byId.get(row.id) ?? curatedAncestor(row);
    if (home && (byId.has(row.id) || !homeOf.has(name))) homeOf.set(name, home);
  }

  const order = (a: Folder, b: Folder) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  const all: Folder[] = [];
  const sortInto = (list: Folder[], depth: number) => {
    list.sort(order);
    for (const folder of list) {
      folder.depth = depth;
      all.push(folder);
      sortInto(folder.children, depth + 1);
    }
  };
  sortInto(roots, 0);
  const notebooks = all.filter((folder) => folder.notebook).sort(order);

  return { roots, byId, byName, homeOf, all, notebooks };
}

/** What the folders root lists: every notebook, then any folder that is
 *  neither a notebook nor inside one — with no curated ancestor it has no
 *  other way to be reached. */
export function rootListing(folders: Folders): Folder[] {
  return [...folders.notebooks, ...folders.roots.filter((folder) => !folder.notebook)];
}

/** A folder's ancestors, outermost first — the crumb, and what a drill-in needs
 *  to say where it is. */
export function trailOf(folders: Folders, folder: Folder): Folder[] {
  const trail: Folder[] = [];
  let at: Folder | undefined = folder;
  const guard = new Set<string>();
  while (at && !guard.has(at.id)) {
    guard.add(at.id);
    trail.unshift(at);
    at = at.parentId ? folders.byId.get(at.parentId) : undefined;
  }
  return trail;
}

/** This folder and everything under it. What a folder HOLDS is its own members;
 *  what it COVERS includes its children's, which is the honest count for a
 *  folder whose whole content sits one level down. */
export function subtreeOf(folder: Folder): Folder[] {
  const out: Folder[] = [];
  const walk = (at: Folder) => {
    out.push(at);
    for (const child of at.children) walk(child);
  };
  walk(folder);
  return out;
}
